import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { T2SEngineAtom, languageAtom, receptionCompanyIdAtom, volumeAtom } from "~/Atom";
import { buildErrorPath } from "~/utils/error";
import {
  getMenuWordsByLanguage,
  mapReceptionLanguage,
  useInactivityTimeout,
} from "~/utils/receptionUtils";
import ReceptionCommunicatingIndicator from "~/components/ReceptionCommunicatingIndicator";
import { getActivationData, getAppSettingsData, getPhoneBooksEntity } from "~/utils/receptionDB";
import type { ActivationData, AppSettingsNormalized, PhoneBooksEntity } from "~/utils/receptionDB";
import { ScreenType, type DeliveryWords } from "~/utils/receptionDB.types";
import { PlaySound } from "~/SoundUtils";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import {
  buildDeliveryReceptionErrorCode,
  resolveCallDestinationAction,
  resolveDeliveryFlowType,
  executeDeliveryChatPost,
  postDeliveryReceptionIgnoreError,
} from "~/utils/deliveryFlowUtils";
import "~/css/reception/DeliveryCompaniesList.css";
import "~/css/reception/common.css";
import "~/css/reception/ReceptionButton.css";
import { ROUTE } from "~/constants/routes";
import { runReceptionBack } from "~/utils/receptionNavigation";
import ReceptionHeader from "~/components/ReceptionHeader";

const NO_CALL_DESTINATION = "B2009";
const FACETIME_PHONEBOOK_CONFLICT = "B2010";

// DeliveryWordsの各タイトルキーを定義（表示順）
const DELIVERY_TITLE_KEYS: (keyof DeliveryWords)[] = [
  "firstDeliveryTitle",
  "secondDeliveryTitle",
  "thirdDeliveryTitle",
  "fourthDeliveryTitle",
  "fifthDeliveryTitle",
  "sixthDeliveryTitle",
  "otherDeliveryTitle",
];

/**
 * 配達会社一覧ページ。
 * Atomの companyId に基づいて DeliveryWords から配達会社ボタンを表示する。
 */
const DeliveryCompaniesListPage: React.FC = () => {
  const [language] = useAtom(languageAtom);
  const [volume] = useAtom(volumeAtom);
  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [receptionCompanyId] = useAtom(receptionCompanyIdAtom);
  const [dbData, setDbData] = useState<AppSettingsNormalized | undefined>(undefined);
  const [activationData, setActivationData] = useState<ActivationData | undefined>(undefined);
  const [phoneBooksEntity, setPhoneBooksEntity] = useState<PhoneBooksEntity | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const isJapanese = language.startsWith("ja");
  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
  });

  const targetGroupDisplayId = receptionCompanyId;

  // IndexedDB からアプリ設定を取得する
  useEffect(() => {
    void getAppSettingsData()
      .then((data) => {
        if (data) setDbData(data);
      })
      .catch((error) => {
        console.error("アプリ設定の読み込みに失敗しました:", error);
      });
  }, []);

  // IndexedDB からアクティベーションデータを取得する
  useEffect(() => {
    void getActivationData()
      .then((data) => {
        if (data) setActivationData(data);
      })
      .catch((error) => {
        console.error("アクティベーションデータの読み込みに失敗しました:", error);
      });
  }, []);

  // IndexedDB から電話帳エンティティを取得する
  useEffect(() => {
    void getPhoneBooksEntity()
      .then((data) => {
        if (data) setPhoneBooksEntity(data);
      })
      .catch((error) => {
        console.error("電話帳エンティティの読み込みに失敗しました:", error);
      });
  }, []);

  // 現在の言語コードに対応するmenuHeaderTitleを取得する
  const instructionText = useMemo((): string => {
    return (
      getMenuWordsByLanguage(dbData?.menuWords, targetGroupDisplayId, language)?.menuHeaderTitle ??
      ""
    );
  }, [dbData, targetGroupDisplayId, language]);

  // 現在の言語コードに対応するDeliveryWordsを取得する
  const deliveryWords = useMemo((): DeliveryWords | null => {
    const langCode = mapReceptionLanguage(language);
    const wordEntity = dbData?.deliveryWords.find((w) => w.id === targetGroupDisplayId);
    return wordEntity?.items.find((i) => i.languageCode === langCode)?.deliveryWords ?? null;
  }, [dbData, targetGroupDisplayId, language]);

  // 空でないタイトルのみ表示するボタン一覧を算出する
  const deliveryButtons = useMemo((): { key: keyof DeliveryWords; label: string }[] => {
    if (!deliveryWords) return [];
    return DELIVERY_TITLE_KEYS.flatMap((key) => {
      const label = deliveryWords[key];
      if (!label || label.trim() === "") return [];
      return [{ key, label }];
    });
  }, [deliveryWords]);

  // id=companyId に対応するmenuButtons設定を取得する（companyIdが0/null/undefinedの場合はid=0にフォールバック）
  const deliveryMenuButtonConfig = useMemo(() => {
    return (
      dbData?.menuButtons.find((b) => b.id === targetGroupDisplayId) ??
      dbData?.menuButtons.find((b) => b.id === 0)
    );
  }, [dbData, targetGroupDisplayId]);

  // id=companyIdかつ現在言語に対応するdeliveryButtonTitleを先に算出する（handleCompanySelectの複雑度を下げる）
  const deliveryButtonName = useMemo((): string => {
    return (
      getMenuWordsByLanguage(dbData?.menuWords, targetGroupDisplayId, language)
        ?.deliveryButtonTitle ?? ""
    );
  }, [dbData, language, targetGroupDisplayId]);

  // id=companyIdに対応するgroupIdを取得する（companyIdが0/null/undefinedの場合はundefined）
  const deliveryGroupId = useMemo((): number | undefined => {
    if (targetGroupDisplayId === 0) return undefined;
    return phoneBooksEntity?.groupList.find((g) => g.displayId === targetGroupDisplayId)?.groupId;
  }, [phoneBooksEntity, targetGroupDisplayId]);

  // id=companyIdに対応するisPhoneBookEnabledを取得する（一致しない場合はid=0にフォールバック）
  const isPhoneBookEnabled = useMemo((): boolean => {
    const basic =
      dbData?.basics.find((b) => b.id === targetGroupDisplayId) ??
      dbData?.basics.find((b) => b.id === 0);
    return basic?.isPhoneBookEnabled ?? false;
  }, [dbData, targetGroupDisplayId]);

  const handleBack = useCallback(() => {
    runReceptionBack({
      kind: "router-history",
      navigate,
    });
  }, [navigate, volume]);

  const handleCompanySelect = useCallback(
    async (label: string) => {
      PlaySound("push", volume);
      if (!activationData || !deliveryMenuButtonConfig) return;

      // plan は ActivationData から取得する
      const plan = activationData.plan.toUpperCase();

      // notificationType・callType・extensionNumber は menuButtons(id=companyId) から取得する
      const notificationType = deliveryMenuButtonConfig.deliveryNotificationType;
      const callType = deliveryMenuButtonConfig.deliveryCallType || null;
      const extensionNumber = deliveryMenuButtonConfig.deliveryExtensionNumber;

      setIsLoading(true);

      // deliveryButtonNameが空の場合はlabel（画面表示テキスト）をフォールバックとして使用
      const buttonName = deliveryButtonName || label;

      const receptionBody: Record<string, unknown> = {
        route: "DELIVERY",
        affiliation: label,
        visitorName: undefined,
        business: undefined,
        groupId: deliveryGroupId,
        groupName: undefined,
        groupUserId: undefined,
        userName: "",
        buttonName,
        notificationType,
      };

      const onPhone = () => navigate(ROUTE.RECEPTION.CALLING, { state: { extensionNumber } });

      const flowType = resolveDeliveryFlowType(plan, notificationType);
      if (flowType === "call") {
        resolveCallDestinationAction(
          callType,
          extensionNumber,
          isPhoneBookEnabled,
          FACETIME_PHONEBOOK_CONFLICT,
          NO_CALL_DESTINATION,
          onPhone,
          navigate
        );
        return;
      }
      if (flowType === "chat") {
        await executeDeliveryChatPost(
          receptionBody,
          () => navigate(ROUTE.RECEPTION.COMPLETE),
          (error) => {
            navigate(
              buildErrorPath(
                buildDeliveryReceptionErrorCode(error, ScreenType.DELIVERY_CONFIRMATION)
              )
            );
          }
        );
        return;
      }
      if (flowType === "chatCall") {
        await postDeliveryReceptionIgnoreError(receptionBody);
        resolveCallDestinationAction(
          callType,
          extensionNumber,
          isPhoneBookEnabled,
          FACETIME_PHONEBOOK_CONFLICT,
          NO_CALL_DESTINATION,
          onPhone,
          navigate
        );
        return;
      }

      setIsLoading(false);
    },
    [
      volume,
      activationData,
      deliveryMenuButtonConfig,
      navigate,
      deliveryButtonName,
      deliveryGroupId,
      isPhoneBookEnabled,
    ]
  );

  // instructionTextをTTSで読み上げる
  useEffect(() => {
    void playWithCooldown(instructionText).catch((error) => {
      console.error("deliveryCompaniesList playVoice failed:", error);
    });
  }, [T2SEngine, instructionText, isJapanese, playWithCooldown]);

  useInactivityTimeout(navigate);

  return (
    <div className="reception-delivery-companies-page">
      <ReceptionHeader showBackButton onBackButtonClick={handleBack} />
      {isLoading && <ReceptionCommunicatingIndicator />}
      <div className="reception-delivery-companies-screen">
        <div className="reception-delivery-companies-screen__content">
          <div className="screen__text">
            <h2 className="screen__title">{instructionText}</h2>
          </div>
          <div className="reception-delivery-companies-screen__container">
            <div className="delivery-companies-grid">
              {deliveryButtons.map(({ key, label }) => (
                <button
                  type="button"
                  key={key}
                  className={`delivery-companies-btn reception-button--large ${key === "otherDeliveryTitle" ? "delivery-companies-btn--full" : ""}`}
                  onClick={() => void handleCompanySelect(label)}
                  aria-label={label}
                  disabled={isLoading}
                >
                  <span className="typo-h3">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryCompaniesListPage;
